import { z } from "zod";
import { getSessionAuthScope, withAuthHeaders } from "../../api/auth";
import { buildDashboard } from "./engine";
import { createDemoDataset } from "./seed";

const NumberOrNull = z.number().finite().nullable();
const Metric = z.object({
  key: z.string(), section: z.string(), label: z.string(), value: NumberOrNull,
  numerator: NumberOrNull, denominator: NumberOrNull, unit: z.string(), explanation: z.string(),
  detail: z.string(), direction: z.string(), quality: z.enum(["demo", "complete", "partial", "unavailable", "insufficient_data", "rules_not_approved", "insufficient_permissions"])
});
const Schema = z.object({
  meta: z.object({ source: z.enum(["demo", "live"]), asOf: z.string(), from: z.string(), to: z.string(), days: z.number(), provider: z.string(), population: z.string().optional(), ruleVersion: z.string(), rulesApproved: z.boolean(), scoringAllowed: z.boolean().optional(), baseline: z.object({ from: z.string(), to: z.string(), documentationMinutes: NumberOrNull, encountersPerDay: NumberOrNull }), available: z.array(z.string()), scope: z.string(), timezone: z.string().nullable().optional(),
    snooze: z.object({ default: z.number(), options: z.array(z.number()), labels: z.array(z.string()) }).optional(),
    rules: z.object({ repeatMisses: z.number(), repeatWindow: z.number(), workloadIncrease: z.number(), sustainedDays: z.number(), afterHoursThreshold: z.number(), weights: z.object({ attendance: z.number(), intake: z.number(), recommendations: z.number() }) }).optional() }),
  providers: z.array(z.object({ id: z.string(), name: z.string() }).passthrough()), metrics: z.array(Metric),
  trends: z.array(z.object({ date: z.string() }).catchall(NumberOrNull)),
  alerts: z.array(z.object({ id: z.string(), title: z.string(), message: z.string(), section: z.string(), type: z.string(), severity: z.string(), status: z.string() }).passthrough()),
  records: z.object({ clinical: z.array(z.object({ id: z.string() }).passthrough()), practice: z.array(z.object({ id: z.string() }).passthrough()), workforce: z.array(z.object({ id: z.string() }).passthrough()), patient: z.array(z.object({ id: z.string() }).passthrough()), admin: z.array(z.object({ id: z.string() }).passthrough()) }),
  today: z.object({ scheduled: z.number(), patients: z.number(), completed: z.number() }),
  operations: z.object({ scheduled: z.number(), completed: z.number(), noShows: z.number(), cancelled: z.number(), previous: z.object({ scheduled: z.number(), completed: z.number(), noShows: z.number(), cancelled: z.number() }) })
});
let demo;
let changes = {};

/**
 * U5: Resolve the effective timezone for date calculations.
 * In live mode, uses the provider's timezone from the dashboard response.
 * In demo mode, uses the demo provider timezone.
 * Falls back to the browser's local timezone.
 */
let _effectiveTimezone = null;
export function setEffectiveTimezone(tz) { _effectiveTimezone = tz; }
export function getEffectiveTimezone() { return _effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone; }

const todayInTimezone = (tz) => new Intl.DateTimeFormat("en-CA", { timeZone: tz || "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const seedDate = () => todayInTimezone("America/New_York"); // Demo always uses ET for deterministic seed
const storageKey = () => "seismic-intelligence-demo-v1:" + seedDate();
function getDemo() {
  if (!demo || demo.seedDate !== seedDate()) {
    demo = createDemoDataset(seedDate());
    try { changes = JSON.parse(sessionStorage.getItem(storageKey()) || "{}"); } catch { changes = {}; }
  }
  return { ...demo,
    gaps: demo.gaps.map(r => ({ ...r, ...(changes.gaps?.[r.id] || {}) })),
    documents: demo.documents.map(r => ({ ...r, ...(changes.documents?.[r.id] || {}) })),
    billing: demo.billing.map(r => ({ ...r, ...(changes.billing?.[r.id] || {}) })),
    alertStates: changes.alerts || {}
  };
}
function saveChanges() {
  try { sessionStorage.setItem(storageKey(), JSON.stringify(changes)); } catch {
    // Demo remains usable in browsers where session storage is disabled.
  }
}
function queryString(filters) { return new URLSearchParams({ date: filters.date, days: String(filters.days), provider: filters.provider, population: filters.population }).toString(); }
async function request(path, filters, options = {}) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const backendToken = sessionStorage.getItem("backendToken");
  const headers = backendToken ? { Authorization: "Bearer " + backendToken } : withAuthHeaders();
  const response = await fetch(base + "/api/intelligence/v1/" + path + "?" + queryString(filters), { ...options, headers: { ...headers, "Content-Type": "application/json" } });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || "Intelligence request failed (" + response.status + ")."), { status: response.status });
  }
  return response.json();
}
export const intelligenceIdentity = () => getSessionAuthScope().doctorId || "current-session";
export async function getDashboard(source, filters, signal) {
  if (source === "demo") return Schema.parse(buildDashboard(getDemo(), filters));
  const data = Schema.parse(await request("dashboard", filters, { signal }));
  // U6: Verify response source matches requested source to prevent demo data leaking as live.
  if (data.meta.source !== "live") throw Object.assign(new Error("Expected live data but received " + data.meta.source + " response."), { status: 502 });
  // U5: Capture provider timezone for date formatting.
  if (data.meta.timezone) setEffectiveTimezone(data.meta.timezone);
  return data;
}
export async function mutateIntelligence(source, filters, { kind, id, action, version, owner, snoozeDuration }) {
  if (source !== "demo") {
    if (kind === "alerts") return request("alerts/" + encodeURIComponent(id), filters, { method: "PATCH", body: JSON.stringify({ status: action, snoozeDuration }) });
    return request("records/" + kind + "/" + encodeURIComponent(id), filters, { method: "PATCH", body: JSON.stringify({ action, version, owner }) });
  }
  const dataset = getDemo();
  if (kind === "alerts") {
    changes.alerts = { ...changes.alerts, [id]: { status: action, snoozedUntil: action === "snoozed" ? new Date(Date.now() + (snoozeDuration || 86400000)).toISOString() : null } };
  } else {
    if (!["gaps", "documents", "billing"].includes(kind)) throw new Error("Unsupported demo action.");
    const record = dataset[kind].find(r => r.id === id);
    if (!record || record.version !== version) throw new Error("This record changed. Close the panel and refresh.");
    let update;
    if (kind === "gaps" && action === "complete") update = { status: "closed", closedAt: seedDate() };
    else if (kind === "gaps" && action === "reopen") update = { status: "open", closedAt: null };
    else if (kind === "gaps" && action === "review") update = { reviewed: true };
    else if (kind === "gaps" && action === "assign") update = { owner };
    else if (kind === "documents" && action === "finalize") update = { status: "finalized", finalizedAt: new Date().toISOString(), afterHours: false };
    else if (kind === "billing" && action === "restore") update = { final: [...record.final, ...record.original.filter(c => c.supported && !record.final.some(f => f.code === c.code && (f.modifier || "") === (c.modifier || "") && (f.units || 1) === (c.units || 1)))] };
    else throw new Error("Unsupported demo action.");
    changes[kind] = { ...changes[kind], [id]: { ...(changes[kind]?.[id] || {}), ...update, version: version + 1 } };
  }
  saveChanges();
}
export function resetDemo() { changes = {}; demo = createDemoDataset(seedDate()); saveChanges(); }
export function exportMetrics(data) {
  const escape = value => '"' + String(value ?? "").replace(/^[=+@-]/, "'$&").replace(/"/g, '""') + '"';
  const lines = [["Source", "From", "To", "Metric", "Value", "Unit", "Numerator", "Denominator", "Quality"], ...data.metrics.map(m => [data.meta.source, data.meta.from, data.meta.to, m.label, m.value, m.unit, m.numerator, m.denominator, m.quality])];
  const url = URL.createObjectURL(new Blob([lines.map(row => row.map(escape).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a"); link.href = url; link.download = "intelligence-" + data.meta.source + "-" + data.meta.to + ".csv"; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** U2: Quality label descriptions for all unavailability reasons. */
export const QUALITY_LABELS = Object.freeze({
  unavailable: "Source not connected",
  rules_not_approved: "Rules not approved",
  insufficient_data: "Insufficient history",
  incomplete_coverage: "Incomplete data coverage",
  insufficient_permissions: "Access restricted",
  demo: "Demonstration data",
  complete: "Verified live data",
  partial: "Partial data available",
});
